<template>
  <auth-card title="Sign in"
    :error-message="errorMessage">
    <new-password-panel v-if="panel === 'newaccount'"
      id-prefix="signin"
      :username="username"
      :is-submitting="isSubmitting"
      @submit="changePassword" />

    <form v-else
      @submit.prevent="signIn">
      <div class="control mb-4">
        <div class="field">
          <label class="label"
            for="companyid">Company ID</label>
          <input id="companyid"
            class="input"
            type="text"
            placeholder="Company ID"
            maxlength="10"
            autocomplete="organization"
            required
            :disabled="isSubmitting"
            v-model="companyLoginId">
          <span v-if="$v.companyLoginId.$dirty && !$v.companyLoginId.required"
            class="help is-danger ml-1">Required</span>
        </div>

        <div class="field">
          <label class="label"
            for="username">Username</label>
          <input id="username"
            class="input"
            type="text"
            placeholder="Username"
            autocomplete="username"
            required
            :disabled="isSubmitting"
            v-model="username">
          <span v-if="$v.username.$dirty && !$v.username.required"
            class="help is-danger ml-1">Required</span>
        </div>

        <!-- login.css .password-container - a deliberate 40px gap before the Remember me row. -->
        <div class="field password-field">
          <label class="label"
            for="password">Password</label>
          <div class="control has-icons-right">
            <input id="password"
              class="input"
              :type="passwordVisible ? 'text' : 'password'"
              placeholder="Password"
              autocomplete="current-password"
              required
              :disabled="isSubmitting"
              v-model="password">
            <span class="icon is-small is-right is-clickable"
              @click="passwordVisible = !passwordVisible">
              <i class="mdi mdi-18px"
                :class="passwordVisible ? 'mdi-eye-outline' : 'mdi-eye-off-outline'" />
            </span>
          </div>
          <span v-if="$v.password.$dirty && !$v.password.required"
            class="help is-danger ml-1">Required</span>
        </div>

        <div class="field remember-me-container
          is-flex is-justify-content-space-between is-align-items-center">
          <label class="checkbox">
            <input type="checkbox"
              :disabled="isSubmitting"
              v-model="rememberMe">
            Remember me
          </label>
          <a @click.prevent="goToForgotPassword">Forgot Password?</a>
        </div>
      </div>

      <div class="control is-flex is-justify-content-end">
        <button type="submit"
          class="button is-primary"
          :disabled="isSubmitting">
          <span class="icon mr-1">
            <i class="mdi mdi-18px"
              :class="isSubmitting ? 'mdi-loading mdi-spin' : 'mdi-arrow-right'" />
          </span>
          Sign In
        </button>
      </div>
    </form>
  </auth-card>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import AuthCard from './AuthCard.vue'
import NewPasswordPanel from './NewPasswordPanel.vue'
import AuthRoutes from './route-types.js'
import { getApiErrorMessage } from './ErrorMessage'
import { resolveAppUrl } from './returnUrl'
import { detectPlatform } from '@/services/platform.js'
import { readLegacyRememberedLogin } from './legacyRememberCookies'

const REMEMBER_KEY = 'fq.rememberedLogin'

export default {
  name: 'SignInView',
  components: {
    AuthCard,
    NewPasswordPanel
  },
  data() {
    return {
      panel: 'login',
      companyLoginId: '',
      username: '',
      password: '',
      passwordVisible: false,
      rememberMe: false,
      // Held only while the first-login password change is in progress.
      passwordChangeTicket: '',
      isSubmitting: false,
      errorMessage: ''
    }
  },
  created() {
    // PCK-655: our own remember-me wins; the legacy cookies only fill the gaps.
    const remembered = this.readRememberedLogin()
    const legacy = readLegacyRememberedLogin()

    this.companyLoginId = remembered.companyLoginId || legacy.companyLoginId
    this.username = remembered.username || legacy.username
    this.rememberMe = !!(this.companyLoginId || this.username)
  },
  methods: {
    readRememberedLogin() {
      try {
        const remembered = JSON.parse(localStorage.getItem(REMEMBER_KEY) || 'null')
        if (remembered) {
          return {
            companyLoginId: (remembered.companyLoginId || '').trim(),
            username: (remembered.username || '').trim()
          }
        }
      } catch (e) { /* corrupt entry - fall through to an empty result */ }
      return { companyLoginId: '', username: '' }
    },

    async signIn() {
      this.errorMessage = ''
      this.$v.$touch()
      if (this.$v.$error) return

      this.isSubmitting = true
      try {
        const result = await this.submitCredentials()

        if (result.requiresPasswordChange) {
          this.passwordChangeTicket = result.ticket
          this.panel = 'newaccount'
          return
        }

        this.rememberCredentials()
        this.enterApp()
      } catch (e) {
        this.errorMessage = getApiErrorMessage(e, 'Sign In failed. Please try again.')
      } finally {
        this.isSubmitting = false
      }
    },

    submitCredentials() {
      const { osId, browserId } = detectPlatform()
      return this.$auth.login({
        companyLoginId: this.companyLoginId,
        username: this.username,
        password: this.password,
        machineId: '',
        osId,
        browserId,
        // Geolocation capture is out of scope for FQ-3057. UserSession.Location is write-only in
        // this codebase, so an empty value costs nothing today.
        location: ''
      })
    },

    async changePassword(newPassword) {
      this.errorMessage = ''
      this.isSubmitting = true
      try {
        await this.$auth.changePassword({
          ticket: this.passwordChangeTicket,
          oldPassword: this.password,
          newPassword
        })

        // The ticket is spent, so sign in again with the new password to get a token.
        this.password = newPassword
        this.passwordChangeTicket = ''
        await this.submitCredentials()

        this.rememberCredentials()
        this.enterApp()
      } catch (e) {
        this.errorMessage = getApiErrorMessage(e, 'Could not change your password. Please try again.')
      } finally {
        this.isSubmitting = false
      }
    },

    rememberCredentials() {
      if (this.rememberMe) {
        localStorage.setItem(REMEMBER_KEY, JSON.stringify({
          companyLoginId: this.companyLoginId,
          username: this.username
        }))
      } else {
        localStorage.removeItem(REMEMBER_KEY)
      }
    },

    enterApp() {
      // Full navigation, not router.push: App.vue runs loadResources once on router.onReady,
      // so an in-app transition would render the shell with no user or permissions loaded.
      window.location.assign(resolveAppUrl(this.$route.query.returnUrl))
    },

    goToForgotPassword() {
      this.$router.push({ name: AuthRoutes.ForgotPasswordView.name })
    }
  },
  validations: {
    companyLoginId: { required },
    username: { required },
    password: { required }
  }
}
</script>

<style lang="scss" scoped>
.label {
  font-weight: 500;
}
.password-field {
  margin-bottom: 40px;
}
.remember-me-container {
  font-size: 0.9rem;
}
</style>
